import React, { useTransition } from 'react';
import HotelDetailSearchBox from './components/HotelDetailSearchBox';
import HotelDetailBreadCrumb from './components/HotelDetailBreadCrumb';
import HotelDetailTitle from './components/HotelDetailTitle';
import HotelDetailGallery from './components/HotelDetailGallery';
import HotelDetailLoadingPage from './components/HotelDetailLoadingPage';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { AppUrls, generateHotelDetailUrlWithFullInfo, generateHotelSearchUrl, getCurrentSearchQuery, getHotelDetailRequest } from '@/urls';
import { apiSdk } from '@/httpClient';
import { Helmet } from 'react-helmet';

import {
  AvailableRoomModel,
  AvailableRoomPlan,
  AvailableRoomType,
  HotelInfoModel,
  HotelSearchRequest,
  PreBookRoomRequest,
  RoomSearchInfo,
} from '@/__generated';
import StickyHeader from './components/StickyHeader';
import HotelLocation from './components/HotelLocation';
import Overview from './components/Overview';
import HotelFacilities from './components/Facilities';
import AvailableRooms from './components/AvailableRooms';
import Spa from './components/Spa';
import SwimmingPool from './components/SwimmingPool';
import DefaultHeader from '@/components/App/Header/DefaultHeader';
import { UNKNOWN_ERROR_MESSAGE, formatDateOnly } from '@/config';
import SearchBoxLoading from './components/SearchBoxLoading';
import BuildingInfo from './components/BuildingInfo';
import AvailableRoomsLoading from './components/AvailableRooms.Loading';
import SearchBox from '@/components/App/SearchBox/SearchBox';
import { scrollToElementById } from '@/helpers/utils';
import SystemAlert from '@/components/App/SystemAlert';
import { ISelectedRoomTypes } from './type';
import { cloneDeep, sortBy } from 'lodash';
import LoadingModal from '@/components/App/Modals/LoadingModal';
import hotelService from '@/services/hotelService';
import { ModalUtils } from '@/contexts/ModalWrapper';
import { useTranslation } from 'react-i18next';
import bookingService from '@/services/bookingService';
import { handleApiUnKnowError, handleHotelNotFound } from '@/helpers/errorHandle';
import { RoomCategoryOrder } from '@/constants';

export default function HotelDetailPage() {
  const history = useHistory();
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState<boolean>(true);
  const [searchRoomLoading, setRoomLoading] = React.useState<boolean>(true);
  const { search } = useLocation();
  const [hotelInfo, setHotelInfo] = React.useState<HotelInfoModel>();
  const { hotelCode, slug } = useParams<{ hotelCode: string; slug: string }>();
  const currentSessionId = React.useRef<string | undefined>(undefined);
  const [availableRooms, setAvailableRooms] = React.useState<AvailableRoomModel[]>([]);
  const [selectedRooms, setSelectedRooms] = React.useState<ISelectedRoomTypes[]>([]);
  const [preBookLoading, setPreBookLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!hotelCode) {
      history.push(AppUrls.HomePage);
      return;
    }
    const roomRequest = getHotelDetailRequest(search);
    if (roomRequest && currentSessionId.current !== roomRequest.sessionId) {
      searchRoomAvailable(
        hotelCode,
        roomRequest.checkinDate,
        roomRequest.checkoutDate,
        roomRequest.boardType,
        roomRequest.rooms,
        roomRequest.sessionId,
        false,
        getCurrentSearchQuery('provider') || undefined,
      );
    }
  }, [search, hotelCode]);

  React.useEffect(() => {
    getHotelDetail(hotelCode);
  }, [hotelCode]);

  const getHotelDetail = (code: string) => {
    setLoading(true);
    apiSdk.BookingService.getHotelDetail({ hotelCode: code })
      .then(res => {
        if (res) {
          setHotelInfo(res);
        } else {
          handleHotelNotFound();
        }
      })
      .catch(handleApiUnKnowError)
      .finally(() => {
        setLoading(false);
      });
  };

  const searchRoomAvailable = (
    hotelCode: string,
    checkinDate: any,
    checkoutDate: any,
    boardType: any,
    rooms: RoomSearchInfo[],
    sessionId: string | undefined,
    scroll2Room?: boolean,
    provider?: string | undefined,
  ) => {
    setRoomLoading(true);
    apiSdk.BookingService.searchHotelRooms({
      body: {
        checkinDate: formatDateOnly(checkinDate) as any,
        checkoutDate: formatDateOnly(checkoutDate) as any,
        hotelCode: hotelCode,
        rooms: rooms.map((r, index) => ({ ...r, roomIndex: index })),
        sessionId: sessionId,
      },
    })
      .then(res => {
        currentSessionId.current = res.sessionId;
        history.push(
          generateHotelDetailUrlWithFullInfo(
            hotelInfo?.nameEn || slug,
            hotelCode,
            checkinDate,
            checkoutDate,
            boardType,
            rooms,
            res.sessionId,
            provider,
          ),
        );
        setSelectedRooms([]);
        res.availableRooms?.forEach(rooms => {
          rooms.availableRoomTypes = sortBy(rooms.availableRoomTypes, x => {
            return RoomCategoryOrder[x.category || ''] || 100;
          });
        });
        setAvailableRooms(res.availableRooms || []);
        if (scroll2Room) {
          scrollToElementById(null, 'rooms');
        }
      })
      .catch(() => {
        //
      })
      .finally(() => {
        setRoomLoading(false);
      });
  };

  const onSearchDetailHotel = (
    hotelName: string,
    hotelCode: string,
    checkinDate: any,
    checkoutDate: any,
    boardType: any,
    rooms: RoomSearchInfo[],
  ) => {
    currentSessionId.current = undefined;
    if (hotelCode !== hotelInfo?.code) {
      history.push(generateHotelDetailUrlWithFullInfo(hotelName, hotelCode, checkinDate, checkoutDate, boardType, rooms));
    } else {
      searchRoomAvailable(hotelCode, checkinDate, checkoutDate, boardType, rooms, undefined, true);
    }
  };

  const onSearchHotel = (request: HotelSearchRequest) => {
    let url = generateHotelSearchUrl(request);
    history.push(url);
  };

  const onSelectedRoomChange = (
    noOfAdult: number,
    noOfChild: number,
    roomType: AvailableRoomType,
    plan: AvailableRoomPlan,
    selectedRoomNumber: number,
  ) => {
    let selectedRoomsClone = hotelService.createSelectedRooms(
      noOfAdult,
      noOfChild,
      roomType,
      plan,
      selectedRoomNumber,
      cloneDeep(selectedRooms) || [],
    );
    setSelectedRooms(selectedRoomsClone);
  };

  const preBook = () => {
    hotelService.checkSelectedRoomsBeforeSubmit(selectedRooms, availableRooms).then(success => {
      if (success) {
        setPreBookLoading(true);
        let preBookRooms: PreBookRoomRequest[] = [];
        selectedRooms.forEach(room => {
          room.roomTypes.forEach(roomType => {
            roomType.plans.forEach(plan => {
              preBookRooms.push({
                noOfAdult: room.noOfAdult,
                noOfChild: room.noOfChild,
                roomPlanId: plan.roomPlanId,
                noOfRooms: plan.selectedRooms,
              });
            });
          });
        });
        apiSdk.BookingService.prebookHotel({
          body: {
            hotelCode: hotelCode,
            sessionId: currentSessionId.current,
            rooms: preBookRooms,
            ...bookingService.getPreBookExtraParams(),
          },
        })
          .then(res => {
            if (res.success) {
              history.push(AppUrls.__createBookingConfirmationUrl(res.sessionId || ''));
            } else if (res.sessionExpired) {
              setPreBookLoading(false);
              ModalUtils.error({
                title: 'Booking session not found!',
                content: (
                  <span>
                    We can not find this booking session, it may be expired or not exist. <br />
                    Go to home page and you can search again!
                  </span>
                ),
                onClose: () => {
                  window.location.reload();
                },
              });
            } else {
              setPreBookLoading(false);
              ModalUtils.error({
                content: t(res.message || 'PrebookHotelFailed'),
              });
            }
          })
          .catch(() => {
            ModalUtils.error({
              content: t('PrebookHotelFailed'),
            });
          })
          .finally(() => {
            setPreBookLoading(false);
          });
      }
    });
  };

  if (loading) {
    return <HotelDetailLoadingPage />;
  }
  return hotelInfo ? (
    <div>
      {preBookLoading ? <LoadingModal title={t('LOADING_MODAL_MESSAGE')} subTitle={t('DO_NOT_CLOSE_BROWSER')} /> : null}
      <Helmet>
        <title>{`${hotelInfo.nameEn} - ${hotelInfo.name}`}</title>
        <meta name="description" content={hotelInfo.description} />
      </Helmet>
      <DefaultHeader />
      {searchRoomLoading ? <SearchBoxLoading /> : <HotelDetailSearchBox hotelInfo={hotelInfo} onSearchDetailHotel={onSearchDetailHotel} />}

      <HotelDetailBreadCrumb hotelInfo={hotelInfo} />
      <SystemAlert className="bg-error-1" />
      <StickyHeader
        preBook={preBook}
        hotelInfo={hotelInfo}
        availableRooms={availableRooms}
        searchRoomLoading={searchRoomLoading}
        selectedRooms={selectedRooms}
      />
      <div className="container mb-50">
        <div className="row x-gap-20 y-gap-20" id="overview">
          <div className="col-md-12 col-sm-12 col-lg-6 col-xl-8">
            <HotelDetailGallery hotelInfo={hotelInfo} />
            <HotelDetailTitle hotelInfo={hotelInfo} />
            <Overview hotelInfo={hotelInfo} />
          </div>
          <div className="col-md-12 col-sm-12 col-lg-6 col-xl-4">
            <HotelLocation hotelInfo={hotelInfo} />
          </div>
        </div>
        <HotelFacilities hotelInfo={hotelInfo} />
        <Spa hotelInfo={hotelInfo} />
        <SwimmingPool hotelInfo={hotelInfo} />
        <div id="rooms" className="pt-20 pb-20">
          {searchRoomLoading ? (
            <AvailableRoomsLoading />
          ) : (
            <>
              {availableRooms.length ? (
                <>
                  <div className="row pb-20 pt-30">
                    <div className="col-auto">
                      <h3 className="text-22 fw-500">Available Rooms</h3>
                    </div>
                  </div>
                  <AvailableRooms
                    selectedRooms={selectedRooms}
                    hotelInfo={hotelInfo}
                    availableRooms={availableRooms}
                    onSelectedRoomChange={onSelectedRoomChange}
                  />
                </>
              ) : (
                <section className="pt-40 border-light rounded-4" style={{ backgroundColor: '#f0f8ff' }}>
                  <div>
                    <div className="text-center">
                      <img className="pb-30 " src="/static/icons/search-hotel.svg" alt="search hotel" style={{ width: 100 }} />
                      <div className="fw-500 text-18">
                        We couldn't find any results that match your search criteria.
                        <br /> Please modify your search and try again.
                      </div>
                    </div>

                    <div className=" mt-10 bg-white px-10 py-10">
                      <div className="col-12">
                        <div className="mainSearch   px-10 py-10 xl:px-20 xl:pt-5 xl:pb-20 rounded-4">
                          <SearchBox onSearchHotel={onSearchHotel} hotelInfo={hotelInfo} onSearchDetailHotel={onSearchDetailHotel} />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
        <BuildingInfo hotelInfo={hotelInfo} />
      </div>
    </div>
  ) : null;
}
